<template>
    <section :class="['inventory-operations-dashboard', { dark: isDark }]">
        <div class="dashboard-page-header">
            <div>
                <p class="dashboard-eyebrow">Focused Dashboard</p>
                <h1>Inventory Operations</h1>
                <p class="dashboard-summary">
                    Complete on-hand inventory coverage with projected reorder, transfer, receiving, and adjustment queues.
                </p>
            </div>
            <div class="dashboard-header-actions">
                <span class="source-pill">Live inventory checks</span>
                <router-link class="dashboard-link" :to="inventoryDrilldownRoute">
                    {{ inventoryDrilldownLabel }}
                    <i class="material-icons">arrow_forward</i>
                </router-link>
            </div>
        </div>

        <div class="status-grid">
            <article v-for="card in statusCards" :key="card.label" :class="['status-card', card.tone]">
                <div class="status-icon">
                    <i class="material-icons">{{ card.icon }}</i>
                </div>
                <div>
                    <p>{{ card.label }}</p>
                    <h2>{{ card.value }}</h2>
                    <span>{{ card.helper }}</span>
                </div>
            </article>
        </div>

        <section class="dashboard-section">
            <div class="section-header">
                <div>
                    <p class="dashboard-eyebrow">Inventory Health</p>
                    <h2>Current quantity and operational pressure</h2>
                </div>
                <span class="section-count">{{ coverageLabel }}</span>
            </div>

            <div v-if="!canViewInventoryReport" class="permission-state">Inventory report permission is required.</div>
            <div v-else-if="loading" class="loading-state">Loading inventory operations...</div>
            <div v-else class="visual-grid">
                <article class="visual-card">
                    <div class="visual-card-header">
                        <div>
                            <h3>Room stock split</h3>
                            <p>Front room, back room, and custom-room units from the complete stock ledger.</p>
                        </div>
                    </div>
                    <div class="visual-frame">
                        <EChartsHorizontalBarChart
                            :categories="roomSplitCategories"
                            :values="roomSplitValues"
                            series-name="Units"
                            aria-label="Inventory room stock split"
                            color="#0d2d2c"
                            dark-color="#4ADE80"
                            :is-dark="isDark"
                            :value-formatter="formatInteger"
                        />
                    </div>
                </article>

                <article class="visual-card">
                    <div class="visual-card-header">
                        <div>
                            <h3>Operations issue mix</h3>
                            <p>Items that may need replenishment, cleanup, receiving, or manager review.</p>
                        </div>
                    </div>
                    <div class="visual-frame">
                        <EChartsHorizontalBarChart
                            :categories="issueMixCategories"
                            :values="issueMixValues"
                            series-name="Open items"
                            aria-label="Inventory operations issue mix"
                            color="#9a6b2f"
                            dark-color="#FBBF24"
                            :is-dark="isDark"
                            :value-formatter="formatInteger"
                        />
                    </div>
                </article>
            </div>
        </section>

        <section class="dashboard-section reorder-planner">
            <div class="section-header">
                <div>
                    <p class="dashboard-eyebrow">Reorder Planning</p>
                    <h2>Projected inventory needs</h2>
                    <p class="section-description">
                        Uses paid net sales velocity and current stock to estimate product-location replenishment.
                    </p>
                </div>
                <span class="section-count">{{ reorderCoverageLabel }}</span>
            </div>

            <div v-if="!canViewReorderRecommendations" class="permission-state">
                Inventory on-hand and Sales by Product report permissions are required.
            </div>
            <template v-else>
                <form class="reorder-controls" @submit.prevent="fetchReorderRecommendations">
                    <label>
                        <span>Location</span>
                        <select v-model="reorderFilters.warehouseId">
                            <option value="">All locations</option>
                            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                                {{ warehouse.name }}
                            </option>
                        </select>
                    </label>
                    <label>
                        <span>Sales history</span>
                        <select v-model.number="reorderFilters.lookbackDays">
                            <option :value="7">7 days</option>
                            <option :value="30">30 days</option>
                            <option :value="60">60 days</option>
                            <option :value="90">90 days</option>
                        </select>
                    </label>
                    <label>
                        <span>Target stock</span>
                        <select v-model.number="reorderFilters.targetDays">
                            <option :value="7">7 days</option>
                            <option :value="14">14 days</option>
                            <option :value="21">21 days</option>
                            <option :value="30">30 days</option>
                        </select>
                    </label>
                    <label>
                        <span>Category</span>
                        <select v-model="reorderFilters.category">
                            <option value="">All categories</option>
                            <option v-for="category in reorderFilterOptions.categories" :key="category" :value="category">
                                {{ category }}
                            </option>
                        </select>
                    </label>
                    <label>
                        <span>Supplier</span>
                        <select v-model="reorderFilters.supplier">
                            <option value="">All suppliers</option>
                            <option v-for="supplier in reorderFilterOptions.suppliers" :key="supplier" :value="supplier">
                                {{ supplier }}
                            </option>
                        </select>
                    </label>
                    <label class="search-control">
                        <span>Product search</span>
                        <input v-model.trim="reorderFilters.search" type="search" placeholder="Name, SKU, brand..." />
                    </label>
                    <div class="reorder-actions">
                        <button class="secondary-action" type="button" :disabled="reorderLoading" @click="resetReorderFilters">
                            Reset
                        </button>
                        <button class="primary-action" type="submit" :disabled="reorderLoading">
                            {{ reorderLoading ? "Calculating..." : "Refresh" }}
                        </button>
                        <button
                            class="secondary-action"
                            type="button"
                            :disabled="reorderLoading || reorderExporting || !reorderRows.length"
                            @click="exportReorderRecommendations"
                        >
                            {{ reorderExporting ? "Exporting..." : "Export Excel" }}
                        </button>
                    </div>
                </form>

                <div v-if="errors.reorder" class="inline-error">Reorder recommendations could not load.</div>
                <div v-else-if="reorderLoading" class="loading-state">Calculating reorder recommendations...</div>
                <template v-else>
                    <div class="reorder-summary-grid">
                        <article>
                            <span>Reorder candidates</span>
                            <strong>{{ formatIntegerValue(reorderSummary.candidateCount) }}</strong>
                        </article>
                        <article>
                            <span>Already out of stock</span>
                            <strong>{{ formatIntegerValue(reorderSummary.outOfStockCount) }}</strong>
                        </article>
                        <article>
                            <span>7 days or less</span>
                            <strong>{{ formatIntegerValue(reorderSummary.urgentCount) }}</strong>
                        </article>
                        <article>
                            <span>Suggested units</span>
                            <strong>{{ formatIntegerValue(reorderSummary.suggestedUnits) }}</strong>
                        </article>
                    </div>

                    <div class="reorder-table-wrap">
                        <table class="status-table reorder-table">
                            <thead>
                                <tr>
                                    <th>Priority</th>
                                    <th>Product</th>
                                    <th>Location</th>
                                    <th>Supplier</th>
                                    <th>On hand</th>
                                    <th>Sold</th>
                                    <th>Daily velocity</th>
                                    <th>Days left</th>
                                    <th>Stockout</th>
                                    <th>Reorder</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in reorderRows" :key="`${row.warehouseId}-${row.itemCode}`">
                                    <td>
                                        <span :class="['status-badge', reorderUrgencyTone(row.urgency)]">
                                            {{ reorderUrgencyLabel(row.urgency) }}
                                        </span>
                                    </td>
                                    <td>
                                        <strong>{{ row.productName }}</strong>
                                        <small>{{ row.itemCode }}<template v-if="row.category"> · {{ row.category }}</template></small>
                                    </td>
                                    <td>{{ row.warehouseName }}</td>
                                    <td>{{ row.supplier || "Not provided" }}</td>
                                    <td>{{ formatDecimalValue(row.onHand) }}</td>
                                    <td>{{ formatDecimalValue(row.unitsSold) }}</td>
                                    <td>{{ formatDecimalValue(row.dailyVelocity) }}/day</td>
                                    <td>{{ formatDaysOfCover(row.daysOfCover) }}</td>
                                    <td>{{ formatDate(row.projectedStockoutDate) }}</td>
                                    <td class="reorder-quantity">{{ formatIntegerValue(row.suggestedReorder) }}</td>
                                </tr>
                                <tr v-if="!reorderRows.length">
                                    <td colspan="10">No products fall below the selected target coverage.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </template>
        </section>

        <div class="detail-grid">
            <section class="dashboard-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">Store Inventory</p>
                        <h2>Stock by location</h2>
                    </div>
                    <router-link class="mini-link" :to="inventoryDrilldownRoute">{{ inventoryDrilldownShortLabel }}</router-link>
                </div>

                <div v-if="!canViewInventoryReport" class="permission-state">Inventory report permission is required.</div>
                <div v-else-if="errors.inventory" class="inline-error">Inventory rows could not load.</div>
                <table v-else class="status-table">
                    <thead>
                        <tr>
                            <th>Store</th>
                            <th>Units</th>
                            <th>Front</th>
                            <th>Back</th>
                            <th>Custom</th>
                            <th>Packages</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in warehouseRows" :key="row.id">
                            <td>{{ row.warehouseName }}</td>
                            <td>{{ formatIntegerValue(row.units) }}</td>
                            <td>{{ formatIntegerValue(row.frontRoomUnits) }}</td>
                            <td>{{ formatIntegerValue(row.backRoomUnits) }}</td>
                            <td>{{ formatIntegerValue(row.customRoomUnits) }}</td>
                            <td>{{ formatIntegerValue(row.packageCount) }}</td>
                        </tr>
                        <tr v-if="!warehouseRows.length">
                            <td colspan="6">No stock rows found.</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section class="dashboard-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">Action Queue</p>
                        <h2>Operational follow-up</h2>
                    </div>
                </div>

                <div class="queue-list">
                    <div v-if="!queueItems.length" class="permission-state">Inventory operations source permissions are required.</div>
                    <router-link
                        v-for="item in queueItems"
                        :key="item.id"
                        :to="item.to"
                        :class="['queue-item', item.tone]"
                    >
                        <div>
                            <strong>{{ item.title }}</strong>
                            <p>{{ item.description }}</p>
                            <span>{{ item.source }}</span>
                        </div>
                        <i class="material-icons">arrow_forward</i>
                    </router-link>
                </div>
            </section>
        </div>

        <div class="detail-grid">
            <section class="dashboard-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">Transfer Aging</p>
                        <h2>Active transfers</h2>
                    </div>
                    <router-link class="mini-link" to="/stock-transfers">Transfers</router-link>
                </div>

                <div v-if="!canViewTransfers" class="permission-state">Transfer permission is required.</div>
                <div v-else-if="errors.transfers" class="inline-error">Active transfers could not load.</div>
                <table v-else class="status-table">
                    <thead>
                        <tr>
                            <th>Transfer</th>
                            <th>Status</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Updated</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="transfer in recentTransfers" :key="transfer.id">
                            <td>
                                <router-link :to="`/stock-transfers/${transfer.id}`">{{ transfer.name }}</router-link>
                            </td>
                            <td><span class="status-badge neutral">{{ transfer.stock_transfer_status || "Unknown" }}</span></td>
                            <td>{{ warehouseNameFromId(transfer.from_warehouse_id) }}</td>
                            <td>{{ warehouseNameFromId(transfer.to_warehouse_id) }}</td>
                            <td>{{ formatDate(transfer.updatedAt || transfer.createdAt || transfer.creation) }}</td>
                        </tr>
                        <tr v-if="!recentTransfers.length">
                            <td colspan="5">No active transfers found.</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section class="dashboard-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">Adjustments</p>
                        <h2>Recent inventory changes</h2>
                    </div>
                    <router-link class="mini-link" to="/inventory-adjustments">Adjustments</router-link>
                </div>

                <div v-if="!canViewAdjustments" class="permission-state">Adjustment permission is required.</div>
                <div v-else-if="errors.adjustments" class="inline-error">Adjustments could not load.</div>
                <table v-else class="status-table">
                    <thead>
                        <tr>
                            <th>Adjustment</th>
                            <th>Type</th>
                            <th>Items</th>
                            <th>Net change</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="adjustment in recentAdjustments" :key="`${adjustment.adjustmentType}-${adjustment.id}`">
                            <td>{{ adjustment.name }}</td>
                            <td>{{ adjustment.adjustmentType }}</td>
                            <td>{{ formatIntegerValue(adjustment.totalItems) }}</td>
                            <td>{{ formatCurrencyValue(adjustment.netChange) }}</td>
                        </tr>
                        <tr v-if="!recentAdjustments.length">
                            <td colspan="4">No recent adjustments found.</td>
                        </tr>
                    </tbody>
                </table>
            </section>
        </div>

        <section class="dashboard-section">
            <div class="section-header">
                <div>
                    <p class="dashboard-eyebrow">Receiving Freshness</p>
                    <h2>Recent stock entries</h2>
                </div>
                <router-link class="mini-link" to="/import-history">Import history</router-link>
            </div>

            <div v-if="!canViewImports" class="permission-state">Stock import history permission is required.</div>
            <div v-else-if="errors.imports" class="inline-error">Stock entry history could not load.</div>
            <table v-else class="status-table">
                <thead>
                    <tr>
                        <th>Entry</th>
                        <th>Supplier</th>
                        <th>Store</th>
                        <th>Tracking</th>
                        <th>Created</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="entry in recentImportEntries" :key="entry.id">
                        <td>
                            <router-link :to="`/import-history/${entry.id}`">{{ entry.name || entry.id }}</router-link>
                        </td>
                        <td>{{ entry.supplier_name || "Not provided" }}</td>
                        <td>{{ entry.target_warehouse_address || "Not provided" }}</td>
                        <td>{{ entry.tracking_number || "Not provided" }}</td>
                        <td>{{ formatDate(entry.createdAt || entry.creation || entry.formattedCreatedAt) }}</td>
                    </tr>
                    <tr v-if="!recentImportEntries.length">
                        <td colspan="5">No recent stock entries found.</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <AnalyticsGoalPanel :is-dark="isDark" active-goal-id="inventory-operations" />
    </section>
</template>

<script>
import { http } from "../../utils/http"
import { mapActions, mapState } from "vuex"
import AnalyticsGoalPanel from "../../components/analytics/AnalyticsGoalPanel.vue"
import EChartsHorizontalBarChart from "../../components/charts/EChartsHorizontalBarChart.vue"
import PriceFetchApi from "../../utils/fetch/price-fetch-api"
import QuantityFetchApi from "../../utils/fetch/quantity-fetch-api"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import {
    summarizeAdjustments,
    summarizeImportEntries,
    summarizeInventoryReportRows,
    summarizeTransfers,
} from "../../utils/inventory-operations-dashboard"

const currencyFormatter = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
})

const integerFormatter = new Intl.NumberFormat("en-CA", {
    maximumFractionDigits: 0,
})

const ACTIVE_TRANSFER_STATUSES = ["PENDING", "SHIPPED", "TRANSFERRED"]
export default {
    name: "InventoryOperationsDashboard",
    components: {
        AnalyticsGoalPanel,
        EChartsHorizontalBarChart,
    },
    data() {
        return {
            loading: true,
            inventoryRows: [],
            inventoryAggregate: null,
            inventoryTotalResults: 0,
            inventoryTotalPages: 0,
            outOfStockRows: [],
            outOfStockTotalResults: 0,
            activeTransfers: [],
            activeTransferTotalPages: 0,
            quantityAdjustments: [],
            priceAdjustments: [],
            importEntries: [],
            reorderRows: [],
            reorderSummary: {
                candidateCount: 0,
                outOfStockCount: 0,
                urgentCount: 0,
                suggestedUnits: 0,
            },
            reorderFilterOptions: {
                categories: [],
                suppliers: [],
            },
            reorderContext: null,
            reorderPagination: {
                totalResults: 0,
                totalPages: 0,
            },
            reorderLoading: false,
            reorderExporting: false,
            reorderFilters: {
                warehouseId: "",
                lookbackDays: 30,
                targetDays: 14,
                category: "",
                supplier: "",
                search: "",
            },
            errors: {
                inventory: false,
                transfers: false,
                adjustments: false,
                imports: false,
                reorder: false,
            },
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES", "warehouses", "transferStatuses"]),
        ...mapState("auth", ["user"]),
        isDark() {
            return this.theme === this.THEMES.DARK
        },
        canViewInventoryReport() {
            return this.hasPermission(PERMISSION_CODES.CMS_VIEW_REPORT_INVENTORY_ON_HAND)
        },
        canViewInventoryManager() {
            return this.hasPermission(PERMISSION_CODES.CMS_VIEW_INVENTORY_MANAGER)
        },
        canViewReorderRecommendations() {
            return this.canViewInventoryReport
                && this.hasPermission(PERMISSION_CODES.CMS_VIEW_REPORT_SALES_BY_PRODUCT)
        },
        canViewTransfers() {
            return this.hasPermission(PERMISSION_CODES.CMS_VIEW_TRANSFER)
        },
        canViewAdjustments() {
            return this.hasPermission(PERMISSION_CODES.CMS_VIEW_ADJUSTMENT)
        },
        canViewImports() {
            return this.hasPermission(PERMISSION_CODES.CMS_VIEW_STOCK_IMPORT_HISTORY)
        },
        inventorySummary() {
            if (this.inventoryAggregate) {
                const summary = this.inventoryAggregate.summary || {}
                return {
                    ...summary,
                    productCount: summary.productLocationCount || 0,
                    lowStockProducts: Array(summary.lowStockCount || 0).fill(null),
                    outOfStockProducts: Array(summary.outOfStockCount || 0).fill(null),
                    lowStockPackages: Array(summary.lowStockPackageCount || 0).fill(null),
                    warehouseRows: this.inventoryAggregate.warehouses || [],
                    estimatedInventoryValue: summary.estimatedCostValue || 0,
                    valuedLedgerEntryCount: summary.valuationCoverage ? 1 : 0,
                }
            }
            return summarizeInventoryReportRows(this.inventoryRows, this.outOfStockRows, {
                lowStockThreshold: 5,
            })
        },
        transferSummary() {
            return summarizeTransfers(this.activeTransfers, {
                staleAfterDays: 3,
            })
        },
        adjustmentSummary() {
            return summarizeAdjustments(this.quantityAdjustments, this.priceAdjustments)
        },
        importSummary() {
            return summarizeImportEntries(this.importEntries)
        },
        statusCards() {
            return [
                {
                    label: "On-hand units",
                    value: this.inventoryStatusValue,
                    helper: this.canViewInventoryReport
                        ? `${this.coverageLabel.toLowerCase()} across ${this.formatIntegerValue(this.inventorySummary.productCount)} product-location groups`
                        : "Requires inventory on-hand report permission",
                    icon: "inventory_2",
                    tone: !this.canViewInventoryReport ? "neutral" : this.errors.inventory ? "danger" : "success",
                },
                {
                    label: "Low stock",
                    value: this.inventoryLowStockValue,
                    helper: this.canViewInventoryReport
                        ? `Complete product-location groups at or below ${this.inventorySummary.lowStockThreshold} units`
                        : "Requires inventory on-hand report permission",
                    icon: "remove_shopping_cart",
                    tone: !this.canViewInventoryReport ? "neutral" : this.inventorySummary.lowStockProducts.length ? "warning" : "success",
                },
                {
                    label: "Estimated stock cost",
                    value: this.inventoryValueStatus,
                    helper: this.canViewInventoryReport
                        ? `${this.formatPercentValue(this.inventorySummary.valuationCoverage)} of ledger packages have valuation rates`
                        : "Requires inventory on-hand report permission",
                    icon: "request_quote",
                    tone: !this.canViewInventoryReport ? "neutral" : this.inventorySummary.valuationCoverage ? "neutral" : "warning",
                },
                {
                    label: "Active transfers",
                    value: this.transferStatusValue,
                    helper: this.canViewTransfers
                        ? `${this.formatIntegerValue(this.transferSummary.staleCount)} loaded transfers are 3+ days old`
                        : "Requires transfer view permission",
                    icon: "local_shipping",
                    tone: !this.canViewTransfers ? "neutral" : this.transferSummary.staleCount ? "warning" : "neutral",
                },
            ]
        },
        inventoryStatusValue() {
            if (!this.canViewInventoryReport) return "No access"
            return this.errors.inventory ? "Error" : this.formatIntegerValue(this.inventorySummary.totalUnits)
        },
        inventoryLowStockValue() {
            if (!this.canViewInventoryReport) return "No access"
            return this.errors.inventory ? "Error" : this.formatIntegerValue(this.inventorySummary.lowStockProducts.length)
        },
        inventoryValueStatus() {
            if (!this.canViewInventoryReport) return "No access"
            if (this.errors.inventory) return "Error"
            return this.inventorySummary.valuedLedgerEntryCount
                ? this.formatCurrencyValue(this.inventorySummary.estimatedInventoryValue)
                : "Needs cost"
        },
        transferStatusValue() {
            if (!this.canViewTransfers) return "No access"
            return this.errors.transfers ? "Error" : this.activeTransferValue
        },
        inventoryDrilldownRoute() {
            return this.canViewInventoryManager ? "/inventory-manager" : "/reports/inventory-on-hand"
        },
        inventoryDrilldownLabel() {
            return this.canViewInventoryManager ? "Inventory manager" : "Inventory report"
        },
        inventoryDrilldownShortLabel() {
            return this.canViewInventoryManager ? "Manage" : "Report"
        },
        coverageLabel() {
            if (!this.inventoryAggregate) return "Source: /analytics/inventory-operations"
            return `${this.formatIntegerValue(this.inventorySummary.packageCount)} complete ledger records · refreshed ${this.formatDate(this.inventoryAggregate.refreshedAt)}`
        },
        reorderCoverageLabel() {
            if (!this.canViewReorderRecommendations) return "Restricted"
            if (!this.reorderContext) return "30-day history · 14-day target"
            return `${this.reorderContext.lookbackDays}-day history · ${this.reorderContext.targetDays}-day target · ${this.formatIntegerValue(this.reorderPagination.totalResults)} matches`
        },
        activeTransferValue() {
            const count = this.formatIntegerValue(this.transferSummary.activeCount)
            return this.activeTransferTotalPages > 1 ? `${count}+` : count
        },
        roomSplitCategories() {
            return ["Front room", "Back room", "Custom rooms"]
        },
        roomSplitValues() {
            return [
                this.inventorySummary.frontRoomUnits,
                this.inventorySummary.backRoomUnits,
                this.inventorySummary.customRoomUnits,
            ]
        },
        issueMixCategories() {
            return ["Low stock", "Out of stock", "Low-stock packages", "Loaded transfers", "Recent adjustments"]
        },
        issueMixValues() {
            return [
                this.inventorySummary.lowStockProducts.length,
                this.outOfStockTotalResults || this.inventorySummary.outOfStockProducts.length,
                this.inventorySummary.lowStockPackages.length,
                this.transferSummary.activeCount,
                this.adjustmentSummary.totalCount,
            ]
        },
        warehouseRows() {
            return this.inventorySummary.warehouseRows.slice(0, 8)
        },
        recentTransfers() {
            return this.transferSummary.recentTransfers
        },
        recentAdjustments() {
            return this.adjustmentSummary.recentAdjustments
        },
        recentImportEntries() {
            return this.importSummary.recentEntries
        },
        queueItems() {
            const items = []

            if (this.canViewInventoryReport) {
                items.push({
                    id: "low-stock",
                    title: "Low stock review",
                    description: this.errors.inventory
                        ? "Inventory health could not be verified."
                        : `${this.inventorySummary.lowStockProducts.length} product-location groups are at or below the current dashboard threshold.`,
                    source: "Source: /analytics/inventory-operations · complete ledger population",
                    to: this.inventoryDrilldownRoute,
                    tone: this.inventorySummary.lowStockProducts.length || this.errors.inventory ? "warning" : "success",
                })
            }

            if (this.canViewTransfers) {
                items.push({
                    id: "transfer-aging",
                    title: "Transfer receiving queue",
                    description: this.errors.transfers
                        ? "Transfer queue could not be verified."
                        : `${this.transferSummary.activeCount} loaded active transfers, ${this.transferSummary.staleCount} aged 3+ days.`,
                    source: "Source: /stock-transfers",
                    to: "/stock-transfers",
                    tone: this.transferSummary.staleCount || this.errors.transfers ? "warning" : "neutral",
                })
            }

            if (this.canViewAdjustments) {
                items.push({
                    id: "adjustments",
                    title: "Adjustment audit",
                    description: this.errors.adjustments
                        ? "Adjustment history could not be verified."
                        : `${this.adjustmentSummary.totalCount} recent quantity or price adjustment headers loaded.`,
                    source: "Source: /qty-adjustments and /price-adjustments",
                    to: "/inventory-adjustments",
                    tone: this.adjustmentSummary.totalCount ? "neutral" : "success",
                })
            }

            if (this.canViewImports) {
                items.push({
                    id: "receiving",
                    title: "Receiving freshness",
                    description: this.importSummary.latestEntry
                        ? `Latest stock entry was created ${this.formatDate(this.importSummary.latestEntry.createdAt || this.importSummary.latestEntry.creation || this.importSummary.latestEntry.formattedCreatedAt)}.`
                        : "No stock entry history is available.",
                    source: "Source: /stock/entries",
                    to: "/import-history",
                    tone: this.errors.imports ? "warning" : "neutral",
                })
            }

            return items
        },
    },
    async mounted() {
        this.setData({ pageTitle: "Inventory Operations" })
        await Promise.all([
            this.fetchInventoryOperations(),
            this.fetchReorderRecommendations(),
        ])
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        hasPermission(permissionId) {
            return checkHasPermission({
                isAdmin: Boolean(this.user?.isAdmin),
                permissionId,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        async fetchInventoryOperations() {
            this.loading = true
            this.errors = {
                ...this.errors,
                inventory: false,
                transfers: false,
                adjustments: false,
                imports: false,
            }

            const activeStatuses = this.getActiveTransferStatuses()
            const transferStatusParams = activeStatuses
                .map(status => `stock_transfer_statuses=${encodeURIComponent(status)}`)
                .join("&")

            const [inventory, transfers, quantityAdjustments, priceAdjustments, imports] = await Promise.allSettled([
                this.canViewInventoryReport
                    ? http.get("/analytics/inventory-operations")
                    : Promise.resolve({ data: { result: null } }),
                this.canViewTransfers
                    ? http.get(`/stock-transfers?page=1&limit=100&${transferStatusParams}`)
                    : Promise.resolve({ data: { result: [], totalPages: 0 } }),
                this.canViewAdjustments
                    ? QuantityFetchApi.getQuantityAdjustments({ page: 1, limit: 50 })
                    : Promise.resolve({ result: [] }),
                this.canViewAdjustments
                    ? PriceFetchApi.getPriceAdjustments({ page: 1, limit: 50 })
                    : Promise.resolve({ result: [] }),
                this.canViewImports
                    ? http.get("/stock/entries")
                    : Promise.resolve({ data: { result: [] } }),
            ])

            if (inventory.status === "fulfilled") {
                this.inventoryAggregate = inventory.value.data.result || null
                this.inventoryRows = []
                this.inventoryTotalResults = this.inventoryAggregate?.summary?.packageCount || 0
                this.inventoryTotalPages = 1
            } else {
                this.inventoryRows = []
                this.inventoryAggregate = null
                this.inventoryTotalResults = 0
                this.inventoryTotalPages = 0
                this.errors.inventory = true
            }

            if (transfers.status === "fulfilled") {
                this.activeTransfers = transfers.value.data.result || []
                this.activeTransferTotalPages = transfers.value.data.totalPages || 0
            } else {
                this.activeTransfers = []
                this.activeTransferTotalPages = 0
                this.errors.transfers = true
            }

            if (quantityAdjustments.status === "fulfilled") {
                this.quantityAdjustments = quantityAdjustments.value.result || []
            } else {
                this.quantityAdjustments = []
                this.errors.adjustments = true
            }

            if (priceAdjustments.status === "fulfilled") {
                this.priceAdjustments = priceAdjustments.value.result || []
            } else {
                this.priceAdjustments = []
                this.errors.adjustments = true
            }

            if (imports.status === "fulfilled") {
                this.importEntries = imports.value.data.result || []
            } else {
                this.importEntries = []
                this.errors.imports = true
            }

            this.loading = false
        },
        buildReorderParams(exportMode = false) {
            const params = {
                warehouse_id: this.reorderFilters.warehouseId || undefined,
                lookback_days: this.reorderFilters.lookbackDays,
                target_days: this.reorderFilters.targetDays,
                category: this.reorderFilters.category || undefined,
                supplier: this.reorderFilters.supplier || undefined,
                search: this.reorderFilters.search || undefined,
                page: 1,
                limit: 500,
            }
            if (exportMode) params.export_mode = true
            return params
        },
        async fetchReorderRecommendations() {
            if (!this.canViewReorderRecommendations) {
                this.reorderRows = []
                return
            }
            this.reorderLoading = true
            this.errors.reorder = false
            try {
                const response = await http.get("/analytics/inventory-reorder", {
                    params: this.buildReorderParams(),
                })
                const result = response.data.result || {}
                this.reorderRows = result.recommendations || []
                this.reorderSummary = result.summary || {
                    candidateCount: 0,
                    outOfStockCount: 0,
                    urgentCount: 0,
                    suggestedUnits: 0,
                }
                this.reorderFilterOptions = result.filterOptions || { categories: [], suppliers: [] }
                this.reorderContext = result.context || null
                this.reorderPagination = result.pagination || { totalResults: 0, totalPages: 0 }
            } catch (error) {
                this.reorderRows = []
                this.reorderContext = null
                this.errors.reorder = true
            } finally {
                this.reorderLoading = false
            }
        },
        resetReorderFilters() {
            this.reorderFilters = {
                warehouseId: "",
                lookbackDays: 30,
                targetDays: 14,
                category: "",
                supplier: "",
                search: "",
            }
            this.fetchReorderRecommendations()
        },
        async exportReorderRecommendations() {
            this.reorderExporting = true
            try {
                const response = await http.get("/analytics/inventory-reorder", {
                    params: this.buildReorderParams(true),
                    responseType: "blob",
                })
                const contentDisposition = response.headers && response.headers["content-disposition"]
                const filenameMatch = contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/)
                const filename = filenameMatch ? filenameMatch[1] : "Inventory_Reorder_Recommendations.xlsx"
                const blob = new Blob([response.data], {
                    type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                })
                const blobUrl = window.URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = blobUrl
                link.download = filename
                link.rel = "noopener noreferrer"
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(blobUrl)
            } catch (error) {
                this.$toast.error("Could not export reorder recommendations.")
            } finally {
                this.reorderExporting = false
            }
        },
        reorderUrgencyLabel(urgency) {
            if (urgency === "out_of_stock") return "Out of stock"
            if (urgency === "urgent") return "Urgent"
            return "Reorder"
        },
        reorderUrgencyTone(urgency) {
            if (urgency === "out_of_stock") return "danger"
            if (urgency === "urgent") return "warning"
            return "neutral"
        },
        formatDecimalValue(value) {
            return Number(value || 0).toLocaleString("en-CA", {
                maximumFractionDigits: 2,
            })
        },
        formatDaysOfCover(value) {
            if (value === null || value === undefined) return "No velocity"
            return `${this.formatDecimalValue(value)} days`
        },
        getActiveTransferStatuses() {
            const statuses = ACTIVE_TRANSFER_STATUSES.map(status => this.transferStatuses?.[status] || status)
            return statuses.filter(Boolean)
        },
        warehouseNameFromId(warehouseId) {
            const warehouse = (this.warehouses || []).find(item => String(item.id) === String(warehouseId))
            if (warehouse?.name) return warehouse.name
            if (!warehouseId) return "-"
            const id = String(warehouseId)
            return id.length > 12 ? `Warehouse ${id.slice(-6)}` : id
        },
        formatCurrency(value) {
            return this.formatCurrencyValue(value)
        },
        formatInteger(value) {
            return this.formatIntegerValue(value)
        },
        formatCurrencyValue(value) {
            return currencyFormatter.format(Number(value) || 0)
        },
        formatIntegerValue(value) {
            return integerFormatter.format(Number(value) || 0)
        },
        formatPercentValue(value) {
            return `${Math.round((Number(value) || 0) * 100)}%`
        },
        formatDate(value) {
            if (!value) return "Not available"
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return String(value)
            return date.toLocaleDateString("en-CA", {
                month: "short",
                day: "numeric",
                year: "numeric",
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@use "@/assets/variables.scss" as *;

.inventory-operations-dashboard {
    color: $textColor;
    padding: $cardPaddingY;
}

.dashboard-page-header {
    align-items: flex-start;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    margin-bottom: 16px;

    h1 {
        color: $primaryColor;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.2;
        margin: 0;
    }
}

.dashboard-eyebrow {
    color: rgba($textColor, 0.56);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    margin: 0 0 4px;
    text-transform: uppercase;
}

.dashboard-summary {
    color: rgba($textColor, 0.68);
    font-size: 13px;
    line-height: 1.5;
    margin: 8px 0 0;
    max-width: 780px;
}

.section-description {
    color: rgba($textColor, 0.62);
    font-size: 12px;
    line-height: 1.45;
    margin: 6px 0 0;
}

.dashboard-header-actions {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.dashboard-link,
.mini-link {
    align-items: center;
    color: $primaryColor;
    display: inline-flex;
    font-size: 13px;
    font-weight: 800;
    gap: 6px;
    text-decoration: none;
    white-space: nowrap;

    i {
        font-size: 18px;
    }

    &:hover {
        color: $secondaryColor;
        text-decoration: none;
    }
}

.source-pill,
.section-count {
    background: rgba($primaryColor, 0.08);
    border-radius: 999px;
    color: $primaryColor;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    padding: 6px 9px;
    text-transform: uppercase;
    white-space: nowrap;
}

.status-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 18px 0 12px;
}

.status-card {
    align-items: flex-start;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-left: 3px solid rgba($primaryColor, 0.3);
    border-radius: 8px;
    box-shadow: $cardShadow;
    display: flex;
    gap: 12px;
    min-height: 118px;
    padding: 14px;

    p {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        font-weight: 700;
        margin: 0 0 7px;
    }

    h2 {
        color: $primaryColor;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.05;
        margin: 0;
    }

    span {
        color: rgba($textColor, 0.64);
        display: block;
        font-size: 11px;
        line-height: 1.35;
        margin-top: 8px;
    }

    &.success {
        border-left-color: $successColor;
    }

    &.warning {
        border-left-color: $warningColor;
    }

    &.danger {
        border-left-color: $dangerColor;
    }
}

.status-icon {
    align-items: center;
    background: rgba($primaryColor, 0.08);
    border-radius: 8px;
    color: $primaryColor;
    display: flex;
    flex: 0 0 34px;
    height: 34px;
    justify-content: center;
    width: 34px;

    i {
        font-size: 19px;
    }
}

.dashboard-section {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    box-shadow: $cardShadow;
    margin-bottom: 16px;
    padding: 16px;
}

.section-header {
    align-items: flex-start;
    display: flex;
    gap: 14px;
    justify-content: space-between;
    margin-bottom: 14px;

    h2 {
        color: $primaryColor;
        font-size: 18px;
        font-weight: 800;
        margin: 0;
    }
}

.visual-grid,
.detail-grid {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.visual-card {
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    padding: 14px;
}

.visual-card-header {
    margin-bottom: 12px;

    h3 {
        color: $primaryColor;
        font-size: 15px;
        font-weight: 800;
        margin: 0 0 4px;
    }

    p {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        line-height: 1.4;
        margin: 0;
    }
}

.visual-frame {
    height: 250px;
    min-width: 0;
}

.status-table {
    border-collapse: collapse;
    width: 100%;

    th,
    td {
        border-bottom: 1px solid $lightBorderColor;
        color: rgba($textColor, 0.76);
        font-size: 12px;
        padding: 10px 8px;
        text-align: left;
        vertical-align: middle;
    }

    th {
        color: rgba($textColor, 0.58);
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
    }

    td:first-child {
        color: $primaryColor;
        font-weight: 800;
    }

    a {
        color: $primaryColor;
        text-decoration: none;

        &:hover {
            color: $secondaryColor;
        }
    }
}

.status-badge {
    border-radius: 999px;
    display: inline-flex;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    padding: 5px 8px;
    text-transform: uppercase;

    &.neutral {
        background: rgba($primaryColor, 0.08);
        color: $primaryColor;
    }

    &.warning {
        background: rgba($warningColor, 0.14);
        color: darken($warningColor, 24%);
    }

    &.danger {
        background: rgba($dangerColor, 0.12);
        color: $dangerColor;
    }
}

.reorder-controls {
    align-items: end;
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(5, minmax(120px, 1fr)) minmax(180px, 1.4fr);
    margin-bottom: 14px;

    label {
        display: grid;
        gap: 5px;
        min-width: 0;

        span {
            color: rgba($textColor, 0.62);
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
        }
    }

    select,
    input {
        background: $lightCardBg;
        border: 1px solid $lightBorderColor;
        border-radius: 7px;
        color: $textColor;
        font-size: 12px;
        height: 38px;
        min-width: 0;
        padding: 0 10px;
        width: 100%;

        &:focus {
            border-color: rgba($primaryColor, 0.5);
            box-shadow: 0 0 0 3px rgba($primaryColor, 0.08);
            outline: none;
        }
    }
}

.reorder-actions {
    display: flex;
    gap: 8px;
    grid-column: 1 / -1;
    justify-content: flex-end;
}

.primary-action,
.secondary-action {
    border: 1px solid $primaryColor;
    border-radius: 7px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 800;
    min-height: 36px;
    padding: 0 14px;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
}

.primary-action {
    background: $primaryColor;
    color: #fff;
}

.secondary-action {
    background: transparent;
    color: $primaryColor;
}

.reorder-summary-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 8px 0 14px;

    article {
        background: rgba($primaryColor, 0.04);
        border: 1px solid $lightBorderColor;
        border-radius: 8px;
        padding: 12px;
    }

    span {
        color: rgba($textColor, 0.62);
        display: block;
        font-size: 11px;
        font-weight: 700;
        margin-bottom: 6px;
    }

    strong {
        color: $primaryColor;
        font-size: 20px;
    }
}

.reorder-table-wrap {
    overflow-x: auto;
}

.reorder-table {
    min-width: 1160px;

    td:nth-child(2) {
        min-width: 210px;
    }

    td:nth-child(2) strong,
    td:nth-child(2) small {
        display: block;
    }

    td:nth-child(2) small {
        color: rgba($textColor, 0.56);
        font-size: 10px;
        font-weight: 500;
        margin-top: 3px;
    }
}

.reorder-quantity {
    color: $primaryColor !important;
    font-size: 15px !important;
    font-weight: 900 !important;
}

.queue-list {
    display: grid;
    gap: 10px;
}

.queue-item {
    align-items: center;
    border: 1px solid $lightBorderColor;
    border-left: 3px solid rgba($primaryColor, 0.3);
    border-radius: 8px;
    color: inherit;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    min-height: 86px;
    padding: 12px;
    text-decoration: none;

    strong {
        color: $primaryColor;
        display: block;
        font-size: 13px;
        line-height: 1.3;
        margin-bottom: 4px;
    }

    p,
    span {
        color: rgba($textColor, 0.62);
        display: block;
        font-size: 11px;
        line-height: 1.35;
        margin: 0;
    }

    span {
        margin-top: 6px;
    }

    i {
        color: $primaryColor;
        flex: 0 0 auto;
        font-size: 18px;
    }

    &.success {
        border-left-color: $successColor;
    }

    &.warning {
        border-left-color: $warningColor;
    }

    &.danger {
        border-left-color: $dangerColor;
    }

    &:hover {
        border-color: rgba($primaryColor, 0.24);
        text-decoration: none;
    }
}

.loading-state,
.permission-state,
.inline-error {
    align-items: center;
    background: rgba($primaryColor, 0.05);
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    color: rgba($textColor, 0.68);
    display: flex;
    font-size: 13px;
    min-height: 84px;
    padding: 16px;
}

.permission-state {
    min-height: 54px;
}

.inline-error {
    background: rgba($dangerColor, 0.08);
    color: $dangerColor;
    min-height: 54px;
}

.dark {
    color: $darkTextColor;

    .dashboard-page-header h1,
    .section-header h2,
    .status-card h2,
    .visual-card-header h3,
    .status-table td:first-child,
    .dashboard-link,
    .mini-link,
    .queue-item strong,
    .queue-item i,
    .status-badge.neutral {
        color: $darkTextColor;
    }

    .dashboard-summary,
    .dashboard-eyebrow,
    .section-description,
    .status-card p,
    .status-card span,
    .visual-card-header p,
    .status-table th,
    .status-table td,
    .queue-item p,
    .queue-item span,
    .loading-state,
    .permission-state {
        color: rgba($darkTextColor, 0.68);
    }

    .dashboard-section,
    .status-card,
    .visual-card,
    .queue-item,
    .loading-state,
    .permission-state {
        background: $darkCardBg;
        border-color: $darkBorderColor;
    }

    .status-table th,
    .status-table td {
        border-color: $darkBorderColor;
    }

    .source-pill,
    .section-count,
    .status-icon,
    .status-badge.neutral {
        background: rgba($darkTextColor, 0.08);
    }

    .reorder-controls label span,
    .reorder-summary-grid span,
    .reorder-table td:nth-child(2) small {
        color: rgba($darkTextColor, 0.68);
    }

    .reorder-controls select,
    .reorder-controls input,
    .reorder-summary-grid article {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }

    .reorder-summary-grid strong,
    .secondary-action,
    .reorder-quantity {
        color: $darkTextColor !important;
    }

    .primary-action {
        background: $darkTextColor;
        border-color: $darkTextColor;
        color: $darkCardBg;
    }

    .secondary-action {
        border-color: $darkTextColor;
    }
}

@media (max-width: 1180px) {
    .status-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .reorder-controls {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 860px) {
    .dashboard-page-header,
    .section-header {
        align-items: stretch;
        flex-direction: column;
    }

    .dashboard-header-actions {
        align-items: flex-start;
    }

    .visual-grid,
    .detail-grid,
    .status-grid,
    .reorder-summary-grid {
        grid-template-columns: 1fr;
    }

    .reorder-controls {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .visual-frame {
        height: 230px;
    }
}

@media (max-width: 560px) {
    .inventory-operations-dashboard {
        padding: 12px;
    }

    .dashboard-section,
    .status-card,
    .visual-card {
        padding: 12px;
    }

    .status-table {
        display: block;
        overflow-x: auto;
        white-space: nowrap;
    }

    .reorder-controls {
        grid-template-columns: 1fr;
    }

    .reorder-actions {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
